import { useCallback, useEffect, useState } from "react";
import { api } from "./client";

interface Result<T> {
  forPath: string | null;
  forVersion: number;
  data?: T;
  error?: string;
}

/**
 * Minimal data-fetching hook: `path === null` skips loading entirely, and
 * `refetch()` re-runs the request. The loading flag is derived from whether
 * the stored result matches the current path/version, so no state needs to be
 * set synchronously inside effects.
 */
export function useApi<T>(path: string | null) {
  const [version, setVersion] = useState(0);
  const [result, setResult] = useState<Result<T> | null>(null);

  const refetch = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (path === null) return;
    let alive = true;
    api<T>(path)
      .then((data) => {
        if (alive) setResult({ forPath: path, forVersion: version, data });
      })
      .catch((err: unknown) => {
        if (alive) {
          setResult({
            forPath: path,
            forVersion: version,
            error: err instanceof Error ? err.message : "Something went wrong.",
          });
        }
      });
    return () => {
      alive = false;
    };
  }, [path, version]);

  const current =
    result && result.forPath === path && result.forVersion === version
      ? result
      : null;

  return {
    data: current?.data,
    error: current?.error,
    loading: path !== null && !current,
    refetch,
  };
}
